import type { IGridColumnSort } from '.';
import type { IGridColumnFilter } from './IGridColumnFilter';
import type { IGridGroupCols } from './IGridGroupCols';

export interface IGridRowGetterParams<TRowData, TParentRowData = any> {
    /** The first row index to get. */
    startRow: number;

    /** The first row index to NOT get. */
    endRow: number;

    /**
     * Callback to call for the result when successful.
     * @param rowsThisBlock The current rows that should be supplied to the grid. This should be a slice of the full dataset starting at {@link startRow} and ending at {@link endRow}
     * @param allRowsFiltered This is the full dataset after filtering has been applied. This should be used to determine the total number of rows in the dataset.
     * @param lastKnownRowInFullDataSet This should be the index of the last known row. Full dataset.length would probably be a good option here. If we are filtering rows, it should be total count AFTER filtering.
     */
    successCallback(rowsThisBlock: TRowData[], allRowsFiltered: TRowData[], lastKnownRowInFullDataSet?: number): void;

    /** How the columns are sorted */
    columnSort: IGridColumnSort[];

    /**
     * Sorts the rows in the dataset in place.
     * Should only be used when we have all the rows locally
     * This should be called after any filtering has been applied
     * @param dataset The dataset to sort
     * @returns nothing since it sorts in place
     */
    sort: (dataset: TRowData[]) => void;

    /**
     * Filters the rows in the dataset in place.
     * Should only be used when we have all the rows locally
     * @param dataset The dataset to filter
     * @returns nothing since it filters in place
     */
    filter: (dataset: TRowData[]) => TRowData[];

    /**
     *
     */
    filters: IGridColumnFilter[];

    /**
     * Contains the list of the current selected groupkeys
     */
    groupKeys?: string[];

    /**
     * Contains the list of the current columns that are grouped
     */
    rowGroupCols?: IGridGroupCols[];

    /**
     * This is the parent row that triggered the row getter
     * This is used when we are getting the child rows in a master detail grid
     */
    parentRow?: TParentRowData;
}

interface IGridDataSource<TRowData> {
    /**
     * Function that is called when the grid wants more rows
     */
    rowGetter: (params: IGridRowGetterParams<TRowData>) => void;

    /**
     * The rows that should be pinned to the top of the grid
     */
    pinnedTopRowData?: TRowData[];

    /**
     * The total number of rows in the dataset, if this is available up front.
     */
    totalNumberOfRows?: () => number;

    /**
     * Function that returns the total number of rows in the dataset.
     * This is used when the total number of rows is not available up front.
     */
    rowCountGetter?: () => number;
}

export default class GridDataSource<TRowData> implements IGridDataSource<TRowData> {
    public rowGetter: (params: IGridRowGetterParams<TRowData>) => void;
    public pinnedTopRowData?: TRowData[];
    public totalNumberOfRows?: () => number;
    public rowCountGetter?: () => number;

    public constructor(dataSource: IGridDataSource<TRowData>) {
        this.rowGetter = dataSource.rowGetter;
        this.pinnedTopRowData = dataSource.pinnedTopRowData;
        this.totalNumberOfRows = dataSource.totalNumberOfRows;
        this.rowCountGetter = dataSource.rowCountGetter;
    }
}
